<div class="emails-page">
  <div class="emails-header">
    <h2>Emails</h2>
    <span class="record-count">{{ totalCount }} records</span>
  </div>

  <div class="filters">
    <form [formGroup]="searchForm" (ngSubmit)="search()">
      <div class="filter-row">
        <div class="filter-field">
          <label>To</label>
          <input
            type="text"
            formControlName="to"
            placeholder="Search by address..."
          />
        </div>
        <div class="filter-field">
          <label>Type</label>
          <select formControlName="emailType" title="Email Type">
            <option value="">All types</option>
            @for (type of emailTypes; track type.value) {
              <option [value]="type.value">{{ type.label }}</option>
            }
          </select>
        </div>
        <div class="filter-field">
          <label>From</label>
          <input type="date" formControlName="createdAtStart" />
        </div>
        <div class="filter-field">
          <label>To</label>
          <input type="date" formControlName="createdAtEnd" />
        </div>
        <div class="filter-actions">
          <button type="submit" class="btn-search">Search</button>
          <button type="button" class="btn-reset" (click)="resetFilters()">
            Reset
          </button>
        </div>
      </div>
    </form>
  </div>

  <div class="table-container">
    @if (loading) {
      <div class="loading-state">
        <div class="loader"></div>
      </div>
    } @else if (emails.length === 0) {
      <div class="empty-state">No emails found</div>
    } @else {
      <table>
        <thead>
          <tr>
            <th class="col-id">ID</th>
            <th class="col-to">To</th>
            <th class="col-type">Type</th>
            <th class="col-sg">SendGrid</th>
            <th class="col-sent">Status</th>
            <th class="col-date">Created</th>
          </tr>
        </thead>
        <tbody>
          @for (email of emails; track email.id) {
            <tr
              class="clickable"
              [class.selected]="selectedEmailId === email.id"
              (click)="openDetail(email.id)"
            >
              <td class="col-id mono">{{ email.id }}</td>
              <td class="col-to mono">{{ email.to ?? '—' }}</td>
              <td class="col-type">
                @if (email.emailType) {
                  <span class="tag tag-type">{{ email.emailType }}</span>
                } @else {
                  <span class="text-dim">&mdash;</span>
                }
              </td>
              <td class="col-sg">
                @if (email.sgMessageId) {
                  <span class="dot dot-active" title="SendGrid"></span>
                } @else {
                  <span class="dot dot-inactive" title="Not SendGrid"></span>
                }
              </td>
              <td class="col-sent">
                @if (email.sent) {
                  <span class="tag tag-sent">Sent</span>
                } @else if (email.unsendable) {
                  <span class="tag tag-failed">Failed</span>
                } @else {
                  <span class="tag tag-pending">Pending</span>
                }
              </td>
              <td class="col-date text-dim">
                {{ formatDate(email.createdAt) }}
              </td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>

  @if (totalPages > 1) {
    <div class="pagination-bar">
      <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
      <div class="page-controls">
        <button
          class="page-btn"
          [disabled]="currentPage === 1"
          (click)="goToPage(currentPage - 1)"
        >
          &larr;
        </button>
        @for (page of pageNumbers; track page) {
          <button
            class="page-btn"
            [class.active]="page === currentPage"
            (click)="goToPage(page)"
          >
            {{ page }}
          </button>
        }
        <button
          class="page-btn"
          [disabled]="currentPage === totalPages"
          (click)="goToPage(currentPage + 1)"
        >
          &rarr;
        </button>
      </div>
    </div>
  }
</div>

<!-- Backdrop -->
@if (drawerOpen) {
  <div class="drawer-backdrop" (click)="closeDrawer()"></div>
}

<!-- Drawer -->
<div class="drawer" [class.open]="drawerOpen">
  <div class="drawer-header">
    <span class="drawer-title">Email #{{ selectedEmailId }}</span>
    <button class="close-btn" (click)="closeDrawer()">&times;</button>
  </div>

  <div class="drawer-body">
    @if (drawerLoading) {
      <div class="loading-state">
        <div class="loader"></div>
      </div>
    } @else if (selectedEmail) {
      <table class="detail-table">
        <tbody>
          <tr>
            <td class="label-cell">ID</td>
            <td class="mono">{{ selectedEmail.id }}</td>
          </tr>
          <tr>
            <td class="label-cell">To</td>
            <td class="mono">{{ selectedEmail.to ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">From</td>
            <td class="mono">{{ selectedEmail.from ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">CC</td>
            <td class="mono">{{ selectedEmail.cc ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">BCC</td>
            <td class="mono">{{ selectedEmail.bcc ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">Subject</td>
            <td>{{ selectedEmail.subject ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">Type</td>
            <td>
              @if (selectedEmail.emailType) {
                <span class="tag tag-type">{{ selectedEmail.emailType }}</span>
              } @else {
                <span class="text-dim">&mdash;</span>
              }
            </td>
          </tr>
          <tr>
            <td class="label-cell">Status</td>
            <td>
              @if (selectedEmail.sent) {
                <span class="tag tag-sent">Sent</span>
              } @else if (selectedEmail.unsendable) {
                <span class="tag tag-failed">Failed</span>
              } @else {
                <span class="tag tag-pending">Pending</span>
              }
            </td>
          </tr>
          <tr>
            <td class="label-cell">SendGrid ID</td>
            <td class="mono">{{ selectedEmail.sgMessageId ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">Template ID</td>
            <td class="mono">{{ selectedEmail.templateId ?? '—' }}</td>
          </tr>
          <tr>
            <td class="label-cell">Created</td>
            <td class="text-dim">{{ formatDate(selectedEmail.createdAt) }}</td>
          </tr>
          <tr>
            <td class="label-cell">Updated</td>
            <td class="text-dim">{{ formatDate(selectedEmail.updatedAt) }}</td>
          </tr>
        </tbody>
      </table>

      <!-- SendGrid Events -->
      <div class="events-section">
        <div class="events-header">
          <span class="events-title">SendGrid Events</span>
          <span class="events-count">{{ sendGridEvents.length }}</span>
        </div>
        @if (sendGridEvents.length === 0) {
          <div class="empty-state">No events recorded</div>
        } @else {
          <table class="events-table">
            <thead>
              <tr>
                <th>Event</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              @for (evt of sendGridEvents; track evt.id) {
                <tr>
                  <td>
                    <span
                      class="tag"
                      [class.tag-sent]="evt.event === 'delivered'"
                      [class.tag-type]="
                        evt.event === 'open' || evt.event === 'click'
                      "
                      [class.tag-pending]="
                        evt.event === 'processed' || evt.event === 'deferred'
                      "
                      [class.tag-failed]="
                        evt.event === 'bounce' ||
                        evt.event === 'dropped' ||
                        evt.event === 'spamreport'
                      "
                      >{{ evt.event }}</span
                    >
                  </td>
                  <td class="text-dim">
                    @if (evt.sgTimestamp) {
                      {{ formatDate(evt.sgTimestamp) }}
                    } @else {
                      &mdash;
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    }
  </div>
</div>
